import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { ArrowLeft, House, X, Crown, Storefront, ChatCircleDots, ChartLine, Cake, Trophy, PlayCircle, Sparkle, ArrowUp, ArrowDown, Minus, HandHeart } from "@phosphor-icons/react";
import CelebrationsCard from "../components/CelebrationsCard";
// Leaderboard sub-tab + district boards moved to LeaderboardPage.js when the
// Leaderboards feature was promoted to a top-level home tile (2026-05-22).

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Activity-based status dot. Reads `last_active_at` and buckets into 4 tiers:
//   - green  : active within the last 15 min
//   - yellow : last seen 15–30 min ago
//   - red    : last seen 30–60 min ago
//   - grey   : >1 hour inactive (or never heartbeated)
// Returns both the color and a short human label for the profile sheet.
const activityStatus = (last_active_at) => {
  if (!last_active_at) return { color: "#94A3B8", label: "Offline" };
  const t = Date.parse(last_active_at);
  if (Number.isNaN(t)) return { color: "#94A3B8", label: "Offline" };
  const minutes = (Date.now() - t) / 60000;
  if (minutes < 15) return { color: "#10B981", label: "Active now" };
  if (minutes < 30) return { color: "var(--fb-accent)", label: "Active recently" };
  if (minutes < 60) return { color: "#F87171", label: "Away" };
  return { color: "#94A3B8", label: "Offline" };
};

const Avatar = ({ member, size = 56, showStatusDot = true }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const initial = (member.first_name || "?").charAt(0).toUpperCase();
  const status = showStatusDot ? activityStatus(member.last_active_at) : null;
  const dotSize = Math.max(10, Math.round(size * 0.28));
  // Centered at the bottom of the avatar so the status reads like a badge
  // rather than an ear — same pattern used in Google Chat / Teams.
  const dot = status && (
    <span
      className="absolute rounded-full ring-2 ring-white"
      style={{
        width: dotSize,
        height: dotSize,
        bottom: -Math.round(dotSize * 0.3),
        left: "50%",
        transform: "translateX(-50%)",
        backgroundColor: status.color,
        boxShadow: status.label === "Active now" ? "0 0 0 0 rgba(16,185,129,0.7)" : "none",
        animation: status.label === "Active now" ? "fbPulseOnline 2s infinite" : "none",
        zIndex: 2,
      }}
      title={status.label}
      data-testid={`avatar-status-${member.id}`}
    />
  );
  if (member.photo_url && !imgFailed) {
    return (
      <span className="relative block leading-none" style={{ width: size, height: size }}>
        <img
          src={member.photo_url}
          alt={member.first_name}
          className="rounded-full object-cover shadow-sm block"
          style={{ width: size, height: size }}
          onError={() => setImgFailed(true)}
        />
        {dot}
      </span>
    );
  }
  return (
    <span className="relative block leading-none" style={{ width: size, height: size }}>
      <span
        className="rounded-full flex items-center justify-center text-white shadow-sm font-black"
        style={{
          width: size,
          height: size,
          backgroundColor: "var(--fb-blue)",
          fontFamily: "'Five Below Gothic', Chivo, sans-serif",
          fontSize: Math.round(size * 0.4),
        }}
      >
        {initial}
      </span>
      {dot}
    </span>
  );
};

// Short role labels — full titles cause name-line wrap on 2-column mobile
// grids. "CEM" / "MM" / "PTSL" etc match how the team refers to roles IRL.
// Store Manager + District Manager shortened to SM / DM 2026-05-25 (user
// ask "lets abbreviate SM and DM on the wow crew right now it shows
// store manager and district manager but its to big").
const SHORT_ROLE = {
  "Store Manager": "SM",
  "District Manager": "DM",
  "Acting SM": "Acting SM",
  "Merchandise Manager": "MM",
  "Customer Experience Manager": "CEM",
  "Full Time Support Lead": "FTSL",
  "Part Time Support Lead": "PTSL",
  "Wow Crew": "Wow Crew",
  "Seasonal Wow Crew": "Seasonal",
};

// Map activityStatus().color (set in this file) to a pair of hex values so we
// can build a matching gradient ring. Both stops stay saturated — a too-pale
// second stop washes the ring out (the green ring looked pastel/faded vs
// the amber/red ones before this). Keep both stops within ~15% lightness
// of each other so the ring reads uniformly bold around the photo.
const RING_GRADIENT = {
  "#10B981": ["#059669", "#10B981"], // green — Active now (saturated emerald)
  "#FACC15": ["#EAB308", "#F59E0B"], // yellow — 15m
  "#F87171": ["#DC2626", "#EF4444"], // red — 30m
  "#94A3B8": ["#64748B", "#94A3B8"], // grey — 1h+
};

// One card per person in the Team Huddle. Photo + role chip + tappable.
// `isLeader` is intentionally unused visually now — leadership is conveyed by
// the "Shift Leaders" section header. The ring color IS the activity status:
// green (active), yellow (15m), red (30m), grey (1h+).
// `isCelebrating` flashes a pink/gold ring for birthdays or work anniversaries.
const HuddleCard = ({ member, onOpen, isLeader = false, isCelebrating = false }) => {
  const role = member.position || "Unassigned";
  const shortRole = SHORT_ROLE[role] || role;
  const status = activityStatus(member.last_active_at);
  const [gA, gB] = RING_GRADIENT[status.color] || RING_GRADIENT["#94A3B8"];
  const staticRingStyle = isCelebrating
    ? {} // background-image set by fbCelebrateRing animation
    : { backgroundImage: `linear-gradient(135deg, ${gA}, ${gB})` };
  return (
    <button
      type="button"
      onClick={() => onOpen(member)}
      className="group relative flex flex-col items-center gap-1.5 rounded-2xl bg-white px-3 py-4 fb-tactile min-w-[120px] sm:min-w-0"
      style={{ border: "1px solid #E5E7EB" }}
      data-testid={`huddle-card-${member.id}`}
      title={status.label}
    >
      {/* Gradient ring hugs the photo directly. Using `flex` (not inline-block
          on the child `<span>`) prevents the ~4px baseline gap that pushes
          the image up in its box and leaks the ring color under the photo. */}
      <div
        className={`rounded-full p-[3px] inline-flex items-center justify-center transition-transform duration-200 group-hover:scale-[1.04] ${isCelebrating ? "fb-celebrate-ring" : ""}`}
        style={staticRingStyle}
        data-testid={`huddle-ring-${member.id}`}
      >
        <Avatar member={member} size={56} showStatusDot={false} />
      </div>
      <p
        className="text-sm font-black truncate w-full text-center text-slate-900 leading-tight mt-1"
        style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}
      >
        {member.first_name}
      </p>
      {/* Role chip — uses SHORT_ROLE so long titles like "Customer Experience
          Manager" don't wrap to 3 lines inside a 120px card. */}
      <span className="text-[9px] font-bold uppercase tracking-wider text-slate-500 truncate max-w-full">
        {shortRole}
      </span>
    </button>
  );
};

// Unified crew profile sheet — single tap on a tile opens THIS sheet
// and it contains everything: avatar + status + motto + highlights +
// full report card stats + message action. Replaces the old two-step
// flow (sheet → tap "Report Card" → modal). (User ask 2026-05-25 "make
// all of it one once you click on dom it should be a nice pop out with
// everything on it - status or how I let go and have fun, highlights
// and report card all on 1 page".)
//
// Stats are fetched lazily when the sheet opens. Highlights strip
// already lazy-fetches itself, so opening costs at most one new GET.
const ProfileActionsSheet = ({ member, viewerId, onClose, onMessage, onHighFive }) => {
  // Report data — null while loading, then an object from /api/crew-report.
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  useEffect(() => {
    if (!member?.id) return undefined;
    let cancelled = false;
    setReportLoading(true);
    axios.get(`${API}/crew-report/${member.id}`)
      .then((r) => { if (!cancelled) setReport(r.data); })
      .catch(() => { /* soft-fail; sheet still works without stats */ })
      .finally(() => { if (!cancelled) setReportLoading(false); });
    return () => { cancelled = true; };
  }, [member?.id]);

  if (!member) return null;
  const status = activityStatus(member.last_active_at);
  // Can't DM yourself — hide the Message action when the viewer lands on their
  // own card (Wow Crew member seeing themselves on the tree, etc).
  const isSelf = !!viewerId && viewerId === member.id;

  // Trend indicator for the stats block. Pulls icon + color from the
  // report payload so the inline view matches the old standalone modal.
  const trendIcon = report?.trend === "improving"
    ? <ArrowUp weight="bold" className="w-4 h-4 text-emerald-500" />
    : report?.trend === "declining"
      ? <ArrowDown weight="bold" className="w-4 h-4 text-red-500" />
      : <Minus weight="bold" className="w-4 h-4 text-slate-400" />;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center"
      onClick={onClose}
      data-testid="profile-actions-backdrop"
    >
      <div
        className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
        data-testid="profile-actions-sheet"
      >
        {/* Hero — blue gradient band with the avatar overlapping. Mirrors
            the founder/report-card visual so the whole app feels like
            one product. (User ask 2026-05-25 "I liked the modern look on
            the profile cards, it should be the window look like we have
            already".) */}
        <div className="relative flex-shrink-0">
          <div
            className="h-24 w-full"
            style={{
              background:
                "linear-gradient(135deg, var(--fb-blue, #0048B3) 0%, #3B82F6 60%, #60A5FA 100%)",
            }}
          />
          <button
            onClick={onClose}
            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center text-white transition-colors z-10"
            aria-label="Close"
            data-testid="profile-actions-close"
          >
            <X weight="bold" className="w-3.5 h-3.5" />
          </button>
          {/* Avatar absolutely overlaps the band (no relative-margin tricks).
              Wrapped in a white ring to match the founder/report-card look. */}
          <div className="absolute left-1/2 -translate-x-1/2 top-14 z-[5] rounded-full bg-white shadow-lg" style={{ padding: 4 }}>
            <Avatar member={member} size={80} showStatusDot={false} />
          </div>
        </div>

        {/* Identity block — name, role, live status. */}
        <div className="px-5 pt-12 pb-1 text-center flex-shrink-0">
          <h3
            className="text-2xl font-bold text-slate-900 leading-tight"
            style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif", letterSpacing: "-0.01em" }}
          >
            {member.first_name}
          </h3>
          <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500 mt-0.5">
            {member.position || "Unassigned"}
          </p>
          <p className="text-[11px] flex items-center justify-center gap-1.5 mt-1.5">
            <span
              className="inline-block w-2 h-2 rounded-full"
              style={{ backgroundColor: status.color }}
            />
            <span className="font-medium" style={{ color: status.color }}>{status.label}</span>
          </p>
        </div>

        {/* Scrollable body — motto, highlights, then full stats. */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* "How do you let go and have fun?" line. */}
          {member.fun_motto && (
            <div
              className="rounded-xl px-4 py-3 border border-blue-100 bg-blue-50/60"
              data-testid={`fun-motto-${member.id}`}
            >
              <p className="text-[10px] uppercase tracking-wider font-semibold text-blue-700 mb-1 flex items-center gap-1.5">
                <Sparkle weight="fill" className="w-3 h-3" />
                How {isSelf ? "I" : member.first_name} {isSelf ? "let" : "lets"} go &amp; ha{isSelf ? "ve" : "s"} fun
              </p>
              <p className="text-sm text-slate-700 leading-snug italic">
                <span className="text-blue-400 mr-1">“</span>{member.fun_motto}<span className="text-blue-400 ml-1">”</span>
              </p>
            </div>
          )}

          {/* Inline highlights strip — lazy-fetches its own data. */}
          <TeamHighlightsStrip memberId={member.id} memberName={member.first_name} viewerId={viewerId} isManager={member.is_manager || false} />

          {/* Inline Report Card stats. */}
          {reportLoading && !report && (
            <div className="space-y-3 animate-pulse" data-testid="profile-report-loading">
              <div className="h-16 rounded-md bg-slate-100" />
              <div className="grid grid-cols-3 gap-3">
                <div className="h-16 rounded-md bg-slate-100" />
                <div className="h-16 rounded-md bg-slate-100" />
                <div className="h-16 rounded-md bg-slate-100" />
              </div>
            </div>
          )}
          {report && (
            <div data-testid="profile-report-inline" className="space-y-3">
              <p className="text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-400">
                Report Card · Email Log &amp; Activity
              </p>
              <div className="grid grid-cols-3 gap-2">
                <StatTile label="Total Emails" value={report.total_emails} />
                <StatTile label="Avg / Day" value={(report.avg_per_day || 0).toFixed(1)} />
                <StatTile label="Days Worked" value={report.days_worked} />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <StatTile label="Best Day" value={report.best_count} accent="amber" />
                <StatTile label="Day Streak" value={report.streak} accent="blue" />
                <StatTile
                  label={`${(report.recent_avg || 0).toFixed(1)} vs ${(report.older_avg || 0).toFixed(1)}`}
                  value={<span className="flex items-center justify-center gap-1 text-sm">{trendIcon}<span className="font-semibold">{report.trend}</span></span>}
                  isCustom
                />
              </div>

              {/* Store-level standings (LW / WTD / MTD). */}
              {report.standings && (
                <div data-testid="profile-report-store-standings">
                  <p className="text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-400 mb-1.5">
                    Your Store · Top 10
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { key: "lw",  label: "LW",  sub: "Last Week" },
                      { key: "wtd", label: "WTD", sub: "Week to Date" },
                      { key: "mtd", label: "MTD", sub: "Month to Date" },
                    ].map(({ key, label, sub }) => {
                      const s = report.standings[key] || {};
                      const inTop = s.in_top_10;
                      const emails = s.emails || 0;
                      return (
                        <div key={key} className="bg-slate-50 rounded-md p-2.5 text-center">
                          <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">{label}</p>
                          <p className="text-lg font-bold leading-none mt-1" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif", color: inTop ? "var(--fb-blue)" : "#94A3B8" }}>
                            {s.rank ? `#${s.rank}` : "—"}
                          </p>
                          <p className="text-[9px] text-slate-500 mt-1">{emails ? `${emails} email${emails === 1 ? "" : "s"}` : sub}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* District-level standings. */}
              {report.district_standings && report.district_standings.total_crew > 0 && (
                <div data-testid="profile-report-district-standings">
                  <p className="text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-400 mb-1.5">
                    District · {report.district_standings.total_crew} crew
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { key: "lw",  label: "LW" },
                      { key: "wtd", label: "WTD" },
                      { key: "mtd", label: "MTD" },
                    ].map(({ key, label }) => {
                      const s = report.district_standings[key] || {};
                      return (
                        <div key={key} className="bg-slate-50 rounded-md p-2.5 text-center">
                          <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">{label}</p>
                          <p className="text-lg font-bold leading-none mt-1" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif", color: s.rank ? "var(--fb-blue)" : "#94A3B8" }}>
                            {s.rank ? `#${s.rank}` : "—"}
                          </p>
                          <p className="text-[9px] text-slate-500 mt-1">
                            {s.percentile ? `Top ${100 - s.percentile + 1}%` : `${s.emails || 0} emails`}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sticky footer — single Message action. Stays in reach when
            stats scroll. Hidden on self-view. */}
        {!isSelf && (
          <div className="px-5 py-3 border-t border-slate-100 bg-white flex-shrink-0 flex gap-2">
            <button
              onClick={() => { onHighFive && onHighFive(member); onClose(); }}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-3 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white font-semibold transition-colors active:scale-[0.98]"
              data-testid="action-high-five"
            >
              <HandHeart weight="fill" className="w-5 h-5" />
              High Five
            </button>
            <button
              onClick={() => { onMessage && onMessage(member); onClose(); }}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-3 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-semibold transition-colors"
              data-testid="action-message"
            >
              <ChatCircleDots weight="fill" className="w-5 h-5" />
              Message
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Tiny stat tile reused inside the inline report block.
function StatTile({ label, value, accent = "slate", isCustom = false }) {
  const palette = {
    slate: { bg: "bg-slate-50", text: "text-slate-900" },
    amber: { bg: "bg-amber-50", text: "text-amber-700" },
    blue:  { bg: "", text: "" }, // custom blue rendered inline below
  }[accent] || { bg: "bg-slate-50", text: "text-slate-900" };
  const style = accent === "blue" ? { backgroundColor: "#DBEAFE" } : undefined;
  const textStyle = accent === "blue" ? { color: "var(--fb-blue)" } : undefined;
  return (
    <div className={`${palette.bg} rounded-md p-2.5 text-center`} style={style}>
      {isCustom ? (
        <div className="text-sm font-semibold" style={textStyle}>{value}</div>
      ) : (
        <p
          className={`text-xl font-bold leading-none ${palette.text}`}
          style={{ ...textStyle, fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}
        >
          {value}
        </p>
      )}
      <p className="text-[10px] text-slate-500 mt-1 uppercase tracking-wider font-semibold" style={textStyle}>{label}</p>
    </div>
  );
}

// Highlights row rendered inside the profile action sheet. Uses the LEAN
// `/highlights?lean=true` payload (no base64 media) so the strip renders
// instantly even on the slowest store wifi — the heavy media only loads on
// tap via /api/highlights/{id}. sessionStorage cache makes repeat opens
// flicker-free. Tapping a thumbnail opens the fullscreen viewer (respects
// delete perms — the viewer can only delete their own or if they're a
// manager).
function TeamHighlightsStrip({ memberId, memberName, viewerId, isManager: viewerIsManager }) {
  const cacheKey = `highlights:lean:${memberId}`;
  const [items, setItems] = useState(() => {
    try {
      const cached = sessionStorage.getItem(cacheKey);
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });
  const [active, setActive] = useState(null);
  const [activeMedia, setActiveMedia] = useState(""); // resolved on tap
  useEffect(() => {
    let dead = false;
    axios.get(`${API}/crew/${memberId}/highlights`, { params: { lean: true } })
      .then((r) => {
        if (dead) return;
        const fresh = r.data || [];
        setItems(fresh);
        try { sessionStorage.setItem(cacheKey, JSON.stringify(fresh)); } catch { /* ignore */ }
      })
      .catch(() => {});
    return () => { dead = true; };
  }, [memberId, cacheKey]);

  // Resolve full media on tap so we never ship 30+ MB up-front.
  const open = async (h) => {
    setActive(h);
    setActiveMedia("");
    try {
      const r = await axios.get(`${API}/highlights/${h.id}`);
      setActiveMedia(r.data?.photo_url || "");
    } catch { setActive(null); }
  };

  const canDelete = (h) => viewerIsManager || h.author_id === viewerId;

  const remove = async (h) => {
    if (!window.confirm("Remove this highlight?")) return;
    try {
      await axios.delete(`${API}/highlights/${h.id}`, { params: { crew_id: viewerId } });
      setItems((prev) => {
        const next = prev.filter((x) => x.id !== h.id);
        try { sessionStorage.setItem(cacheKey, JSON.stringify(next)); } catch { /* ignore */ }
        return next;
      });
      setActive(null);
      setActiveMedia("");
    } catch (e) {
      /* silent — sheet already closing */
    }
  };

  return (
    <div className="pb-2 mb-1" data-testid={`team-highlights-${memberId}`}>
      <p className="text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1.5">{memberName}'s Highlights</p>
      {items.length ? (
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {items.map((h) => (
            <button
              key={h.id}
              type="button"
              onClick={() => open(h)}
              className="flex-shrink-0 w-14 h-14 rounded-full overflow-hidden ring-2 ring-slate-200 hover:ring-blue-400 bg-slate-100 focus:outline-none transition-all relative"
              data-testid={`team-highlight-${h.id}`}
              title={h.caption || h.collection_name || "Story"}
            >
              {h.thumb_url ? (
                <img src={h.thumb_url} alt="" className="w-full h-full object-cover" />
              ) : h.media_type === "video" ? (
                <div className="w-full h-full bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center">
                  <PlayCircle weight="fill" className="w-6 h-6 text-white/90" />
                </div>
              ) : (
                <div className="w-full h-full bg-slate-200" />
              )}
              {h.media_type === "video" && h.thumb_url && (
                <span className="absolute inset-0 flex items-center justify-center bg-black/20">
                  <PlayCircle weight="fill" className="w-5 h-5 text-white drop-shadow" />
                </span>
              )}
            </button>
          ))}
        </div>
      ) : (
        <p className="text-xs text-slate-400 italic pb-1">No saved stories yet.</p>
      )}
      {active && (
        <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4" onClick={() => { setActive(null); setActiveMedia(""); }}>
          <div className="relative w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            {activeMedia ? (
              active.media_type === "video" ? (
                <video src={activeMedia} controls autoPlay playsInline className="w-full rounded-xl" />
              ) : (
                <img src={activeMedia} alt="" className="w-full rounded-xl" />
              )
            ) : (
              <div className="w-full aspect-[4/5] rounded-xl bg-slate-800 animate-pulse" />
            )}
            {active.caption && (
              <div className="mt-3 text-white text-sm bg-black/40 rounded-lg px-3 py-2">{active.caption}</div>
            )}
            {active.collection_name && (
              <div className="mt-1 text-yellow-300 text-[11px] uppercase tracking-wider font-black">★ {active.collection_name}</div>
            )}
            <div className="mt-3 flex items-center justify-between text-white">
              <button onClick={() => { setActive(null); setActiveMedia(""); }} className="text-xs font-bold underline">Close</button>
              {canDelete(active) && (
                <button onClick={() => remove(active)} className="text-xs font-bold text-rose-300 hover:text-rose-200">
                  Remove
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Unified member tile used by BOTH leaders and crew (2026-02-22) -------
// User feedback "I don't like how leadership went to circles and the crew
// stayed boxes" — same shape for everyone, only the section header marks
// the role. Variants:
//   default → grid cell (auto-sized by parent grid)
//   scroll  → fixed-width tile for the horizontal Shift Leaders row.
// `isLeader` adds a Five Below blue gradient ring around the avatar so a
// leader still reads as "different" without breaking visual consistency.
function MemberTile({ member, onOpen, isCelebrating, isLeader = false, variant = "default" }) {
  const role = member.position || (isLeader ? "Leader" : "Crew");
  const shortRole = SHORT_ROLE[role] || role;
  const wrapperClass = variant === "scroll"
    ? "flex-shrink-0 w-24 sm:w-28 snap-start"
    : "";
  const ringClass = isCelebrating
    ? "fb-celebrate-ring"
    : isLeader
      ? "bg-gradient-to-br from-blue-600 to-blue-400"
      : "bg-slate-100";
  const ringShadow = isLeader && !isCelebrating
    ? { boxShadow: "0 4px 12px rgba(0,102,255,0.18)" }
    : {};
  return (
    <div className={wrapperClass}>
      <button
        type="button"
        onClick={() => onOpen(member)}
        className="group bg-white rounded-2xl shadow-sm hover:shadow-md transition-all p-3 flex flex-col items-center gap-2 fb-tactile w-full"
        data-testid={isLeader ? `leader-tile-${member.id}` : `crew-card-${member.id}`}
      >
        <div className={`rounded-full p-[2px] ${ringClass}`} style={ringShadow}>
          <Avatar member={member} size={56} showStatusDot />
        </div>
        <p
          className="text-sm font-black truncate w-full text-center text-slate-900 leading-tight"
          style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}
        >
          {member.first_name}
        </p>
        <span
          className={`text-[9px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 truncate max-w-full ${
            isLeader ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-600"
          }`}
        >
          {shortRole}
        </span>
        {/* Tenure pill — surfaces at-a-glance how long this crew member has
            been on the team. Drives off `member.hire_date` (YYYY-MM-DD) so
            it stays in sync the moment an admin updates dates via the
            Bulk Hire Dates card or a self-edit. Hidden until a date is
            set so unset crew don't show "0d" noise. (User ask 2026-05-08
            "I can't see these anniversary dates even as an admin on the
            wow crew page".) */}
        {member.hire_date && (() => {
          const d = new Date(`${member.hire_date}T00:00:00`);
          if (isNaN(d.getTime())) return null;
          const months = Math.max(0, Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24 * 30.4375)));
          const years = Math.floor(months / 12);
          const label = years >= 1
            ? `${years}y${months % 12 ? ` ${months % 12}mo` : ""}`
            : months >= 1
              ? `${months}mo`
              : "New";
          return (
            <span className="text-[9px] font-bold rounded-full px-2 py-0.5 bg-amber-50 text-amber-700" data-testid={`tenure-${member.id}`}>
              ⭐ {label}
            </span>
          );
        })()}
      </button>
    </div>
  );
}

// Celebrations tab — gradient hero card when there's a celebration TODAY,
// otherwise the existing CelebrationsCard for the upcoming list. Pink-yellow
// gradient sells the moment without needing a heavy background image
// (perf-friendly on Zebra scanners).
function CelebrationsTabContent({ celebs }) {
  const hasToday = celebs.birthdays.length > 0 || celebs.anniversaries.length > 0;
  return (
    <section className="space-y-4 fb-enter" style={{ "--fb-delay": "40ms" }} data-testid="team-celebrations-section">
      {hasToday && (
        <div
          className="rounded-3xl p-5 text-white shadow-xl relative overflow-hidden"
          style={{ background: "linear-gradient(135deg,#EC4899 0%,#F43F5E 50%,var(--fb-accent) 100%)" }}
          data-testid="celebration-today"
        >
          <div className="absolute -top-8 -right-8 opacity-20 pointer-events-none">
            <Cake weight="duotone" className="w-40 h-40 text-white" />
          </div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-black opacity-90">Today</p>
          <h2
            className="text-2xl sm:text-3xl font-black leading-tight mt-1 max-w-[80%]"
            style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif", letterSpacing: "-0.02em" }}
          >
            {celebs.birthdays.length > 0 && celebs.anniversaries.length > 0
              ? "Big day around here!"
              : celebs.birthdays.length > 0
                ? `Happy Birthday, ${celebs.birthdays.map((b) => b.first_name).join(" & ")}!`
                : `Cheers to ${celebs.anniversaries.map((a) => `${a.first_name} (${a.years}y)`).join(" & ")}!`}
          </h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {celebs.birthdays.map((b) => (
              <span key={`b-${b.id}`} className="inline-flex items-center gap-1.5 bg-white/25 backdrop-blur rounded-full px-3 py-1 text-xs font-bold">
                <Cake weight="fill" className="w-3.5 h-3.5" />
                {b.first_name}'s birthday{b.store_name ? ` · ${b.store_name}` : ""}
              </span>
            ))}
            {celebs.anniversaries.map((a) => (
              <span key={`a-${a.id}`} className="inline-flex items-center gap-1.5 bg-white/25 backdrop-blur rounded-full px-3 py-1 text-xs font-bold">
                <Trophy weight="fill" className="w-3.5 h-3.5" />
                {a.first_name} · {a.years} year{a.years === 1 ? "" : "s"}{a.store_name ? ` · ${a.store_name}` : ""}
              </span>
            ))}
          </div>
        </div>
      )}
      {/* Upcoming list — left-border accent in Five Below blue per design spec. */}
      <div className="bg-white rounded-2xl shadow-sm p-1 border-l-4 border-[var(--fb-blue)]">
        <CelebrationsCard />
      </div>
    </section>
  );
}


export default function TeamPage({ user, crew = [], onMount, onBack, onGoHome, onMessage, onHighFive, onGoToCalendar }) {
  const [openMember, setOpenMember] = useState(null);
  // Tabs removed 2026-05-22 — Leaderboards was promoted to its own home
  // tile, so this page is single-purpose (team huddle) again.
  // Pulled once on mount so the "Store {N}" header label reflects the admin-
  // editable Store Info setting (instead of hard-coded "3094"). Falls back
  // to the per-user `home_store_code`/`home_store_city` when present so
  // mirror mode AND sister-store crew get the right store label without
  // a settings round-trip. (Bug fix 2026-05-20: mirror was showing
  // "STORE 3094 · 0 ACTIVE" while viewing 3010 Littleton.)
  const [storeInfo, setStoreInfo] = useState({
    store_number: user?.home_store_code || "3094",
    store_city: user?.home_store_city || "Wheat Ridge",
  });

  // Re-pull the roster when this page opens so any recent profile-photo edits
  // flow through to the huddle (profile photos live on the crew doc and are
  // cached at the app level via refreshCrew in App.js).
  useEffect(() => { onMount && onMount(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => {
    // If we already have a real store code from the auth payload (the
    // common path post-2026-05-19 multi-tenant rollout), trust that — the
    // /api/settings doc is global and would lie for sister-store users.
    if (user?.home_store_code) {
      setStoreInfo({
        store_number: user.home_store_code,
        store_city: user.home_store_city || "",
      });
      return;
    }
    axios.get(`${API}/settings`).then((r) => {
      setStoreInfo({
        store_number: r.data?.store_number || "3094",
        store_city: r.data?.store_city || "Wheat Ridge",
      });
    }).catch(() => {});
  }, [user?.home_store_code, user?.home_store_city]);

  // "Today's celebrations" badge in the header — pulls the MST-aware list once
  // on mount. Shows a confetti-chip when there's at least one birthday or
  // anniversary today.
  const [todayCelebs, setTodayCelebs] = useState({ birthdays: [], anniversaries: [] });
  useEffect(() => {
    axios
      .get(`${API}/celebrations/today`)
      .then((r) => setTodayCelebs(r.data || { birthdays: [], anniversaries: [] }))
      .catch(() => {});
  }, []);

  // Group active crew into Shift Leaders (managers) + Crew (non-managers).
  // The old tiered tree is gone — we now present a "team huddle": leaders
  // across the top, crew in a grid below. Position tags still show on each
  // card so the role hierarchy is visible without a diagram.
  const LEADER_POSITIONS = useMemo(() => new Set([
    "Store Manager",
    "Merchandise Manager",
    "Customer Experience Manager",
    "Full Time Support Lead",
    "Part Time Support Lead",
  ]), []);
  const { districtLeaders, leaders, crewMembers, unassigned, celebratingIds, dmIsActingSM } = useMemo(() => {
    const active = (crew || []).filter((c) => c.active !== false);
    const dms = [];
    const l = [];
    const cr = [];
    const un = [];
    for (const c of active) {
      // District Manager — pulled into a dedicated section above Leaders
      // so the district hierarchy is visually obvious. (User ask
      // 2026-05-23: "DM position on the wow crew title should be above
      // leadership since she is the DM right now you have her on the
      // same line with store management".)
      if (c.is_district_manager) {
        dms.push({ ...c, position: c.position || "District Manager" });
        continue;
      }
      if (!c.position) un.push(c);
      else if (LEADER_POSITIONS.has(c.position)) l.push(c);
      else cr.push(c);
    }
    dms.sort((a, b) => (a.first_name || "").localeCompare(b.first_name || ""));
    l.sort((a, b) => {
      if (a.position === "Store Manager" && b.position !== "Store Manager") return -1;
      if (b.position === "Store Manager" && a.position !== "Store Manager") return 1;
      return (a.first_name || "").localeCompare(b.first_name || "");
    });
    cr.sort((a, b) => (a.first_name || "").localeCompare(b.first_name || ""));
    un.sort((a, b) => (a.first_name || "").localeCompare(b.first_name || ""));
    // Merge birthdays + anniversaries — both deserve the flashing ring.
    const celebSet = new Set([
      ...(todayCelebs.birthdays || []).map((b) => b.id),
      ...(todayCelebs.anniversaries || []).map((a) => a.id),
    ]);
    // DM inheritance: if this store has NO Store Manager assigned and a
    // DM is present in the crew list (the backend injects her on every
    // sister store's roster), she's the acting SM for this store. We
    // relabel the District Manager section accordingly so crew know
    // who's on point until a real SM is hired. (User ask 2026-05-25.)
    const hasStoreManager = l.some((m) => (m.position || "").trim() === "Store Manager");
    const actingSM = !hasStoreManager && dms.length > 0;
    return { districtLeaders: dms, leaders: l, crewMembers: cr, unassigned: un, celebratingIds: celebSet, dmIsActingSM: actingSM };
  }, [crew, LEADER_POSITIONS, todayCelebs.birthdays, todayCelebs.anniversaries]);

  return (
    <div
      className="min-h-screen relative"
      style={{
        background:
          "radial-gradient(1000px 700px at 50% -10%, rgba(59,130,246,0.10), transparent 60%), radial-gradient(800px 600px at 85% 100%, rgba(236,72,153,0.08), transparent 60%), #F8FAFC",
        color: "#0F172A",
      }}
      data-testid="team-page"
    >
      <header
        className="sticky top-0 z-30 backdrop-blur-xl"
        style={{
          backgroundColor: "rgba(248,250,252,0.85)",
          borderBottom: "1px solid rgba(148,163,184,0.18)",
        }}
      >
        <div className="max-w-5xl mx-auto px-4 h-12 flex items-center gap-2">
          <button
            onClick={onBack}
            className="p-1 rounded-full hover:bg-slate-100 transition-colors"
            aria-label="Back"
            data-testid="team-back"
          >
            <ArrowLeft weight="bold" className="w-5 h-5 text-slate-700" />
          </button>
          <div className="flex-1 min-w-0">
            <h1
              className="text-base font-black leading-none text-slate-900"
              style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif", letterSpacing: "-0.01em" }}
              data-testid="team-page-title"
            >
              Wow Crew
            </h1>
            <p className="text-[9px] uppercase tracking-[0.14em] text-slate-500 font-semibold mt-0.5">
              Store {storeInfo.store_number} · {leaders.length + crewMembers.length + unassigned.length} active
            </p>
          </div>
          {/* Today celebration chips — pink cake for birthdays, amber trophy for
              anniversaries. Shows the first celebrant's first name; additional
              celebrants are surfaced as a "+N" counter. Tapping scrolls to the
              Celebrations card. */}
          {(todayCelebs.birthdays.length > 0 || todayCelebs.anniversaries.length > 0) && (
            <button
              onClick={() => onGoToCalendar && onGoToCalendar()}
              className="flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-bold shadow-sm hover:shadow transition-all"
              style={{
                background: "linear-gradient(135deg,#FCE7F3,#FEF3C7)",
                borderColor: "rgba(236,72,153,0.35)",
                color: "#9D174D",
              }}
              data-testid="today-celebration-badge"
              title={[
                ...todayCelebs.birthdays.map((b) => `${b.first_name}'s birthday`),
                ...todayCelebs.anniversaries.map((a) => `${a.first_name} · ${a.years} yr`),
              ].join(" · ")}
            >
              {todayCelebs.birthdays.length > 0 && <Cake weight="fill" className="w-3 h-3 text-pink-600" />}
              {todayCelebs.anniversaries.length > 0 && <Trophy weight="fill" className="w-3 h-3 text-amber-600" />}
              <span className="hidden sm:inline">
                {todayCelebs.birthdays[0]?.first_name || todayCelebs.anniversaries[0]?.first_name}
                {todayCelebs.birthdays.length + todayCelebs.anniversaries.length > 1 && (
                  <span className="ml-1 text-[10px] font-semibold">+{todayCelebs.birthdays.length + todayCelebs.anniversaries.length - 1}</span>
                )}
              </span>
              <span className="sm:hidden text-[10px] font-black">TODAY</span>
            </button>
          )}
          <button
            onClick={onGoHome}
            className="p-1 rounded-full hover:bg-slate-100 transition-colors"
            aria-label="Home"
            data-testid="team-home"
          >
            <House weight="bold" className="w-5 h-5 text-slate-700" />
          </button>
        </div>
      </header>

      <main className="relative max-w-5xl mx-auto px-3 sm:px-4 py-4 pb-16 space-y-5">
        {/* Leaderboard sub-tab removed 2026-05-22 — Leaderboards lives on
            its own home tile now, visible to everyone. This page focuses
            purely on the team huddle (shift leaders + crew grid). */}

        {/* District Manager — own section above Shift Leaders so the
            district hierarchy reads top-down. (User ask 2026-05-23.)
            When the store has no Store Manager, the section retitles as
            "Acting Store Manager" with an amber tag so crew know who's
            on point. (User ask 2026-05-25.) */}
        {districtLeaders.length > 0 && (
          <section className="fb-enter" style={{ "--fb-delay": "60ms" }} data-testid={dmIsActingSM ? "acting-sm-section" : "district-manager-section"}>
            <div className="flex items-center justify-between mb-3 px-1">
              <h2 className={`text-[10px] font-black uppercase tracking-[0.18em] ${dmIsActingSM ? "text-amber-700" : "text-amber-700"} flex items-center gap-2`}>
                {dmIsActingSM ? (
                  <>
                    <span>Acting Store Manager · {districtLeaders.length}</span>
                    <span
                      className="text-[9px] font-bold text-amber-800 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded normal-case tracking-normal"
                      data-testid="dm-acting-sm-chip"
                    >
                      District inherit
                    </span>
                  </>
                ) : (
                  <span>District Manager · {districtLeaders.length}</span>
                )}
              </h2>
            </div>
            <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 scrollbar-hide -mx-3 px-3" data-testid="district-manager-row">
              {districtLeaders.map((m) => (
                <MemberTile
                  key={m.id}
                  member={dmIsActingSM ? { ...m, position: "Acting SM" } : m}
                  onOpen={setOpenMember}
                  isCelebrating={celebratingIds.has(m.id)}
                  isLeader
                  variant="scroll"
                />
              ))}
            </div>
          </section>
        )}

        {/* Shift Leaders — Instagram Stories-style horizontal scroll. Each
            leader is a circular avatar with a Five Below blue ring, name +
            short role under. Snap-x so taps land cleanly on each card. */}
        {leaders.length > 0 && (
          <section className="fb-enter" style={{ "--fb-delay": "100ms" }} data-testid="shift-leaders-section">
            <div className="flex items-center justify-between mb-3 px-1">
              <h2 className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">
                Shift Leaders · {leaders.length}
              </h2>
            </div>
            <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 scrollbar-hide -mx-3 px-3" data-testid="shift-leaders-row">
              {leaders.map((m) => (
                <MemberTile
                  key={m.id}
                  member={m}
                  onOpen={setOpenMember}
                  isCelebrating={celebratingIds.has(m.id)}
                  isLeader
                  variant="scroll"
                />
              ))}
            </div>
          </section>
        )}

        {/* Wow Crew — Bento Grid. 3 cols on mobile, 4 on sm, 5 on md. Each
            tile is a centered avatar + name + tiny position pill, fully
            tappable. Killing the long scroll: 12 crew members fit in 3-4
            rows now instead of 12 vertical cards. */}
        {crewMembers.length > 0 && (
          <section className="fb-enter" style={{ "--fb-delay": "160ms" }} data-testid="crew-grid-section">
            <div className="flex items-center justify-between mb-3 px-1">
              <h2 className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">
                Wow Crew · {crewMembers.length}
              </h2>
            </div>
            <div
              className="grid gap-3 grid-cols-3 sm:grid-cols-4 md:grid-cols-5"
              data-testid="crew-grid"
            >
              {crewMembers.map((m) => (
                <MemberTile
                  key={m.id}
                  member={m}
                  onOpen={setOpenMember}
                  isCelebrating={celebratingIds.has(m.id)}
                />
              ))}
            </div>
          </section>
        )}

        {/* Anyone without a position — compact list at the bottom (saves
            vertical space; this group is rare and rarely tapped). */}
        {unassigned.length > 0 && (
          <section
            className="pt-4 mt-2"
            style={{ borderTop: "1px dashed rgba(100,116,139,0.25)" }}
            data-testid="tier-unassigned"
          >
            <p className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
              Needs a position · {unassigned.length}
            </p>
            <div className="flex flex-wrap gap-2">
              {unassigned.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setOpenMember(m)}
                  className="flex items-center gap-2 px-2.5 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors"
                  data-testid={`unassigned-${m.id}`}
                >
                  <Avatar member={m} size={20} showStatusDot={false} />
                  <span className="text-xs font-bold text-slate-700">{m.first_name}</span>
                </button>
              ))}
            </div>
          </section>
        )}
      </main>

      {openMember && (
        <ProfileActionsSheet
          member={openMember}
          viewerId={user?.crew_id}
          onClose={() => setOpenMember(null)}
          onMessage={(m) => { onMessage && onMessage(m.id); }}
          onHighFive={(m) => { onHighFive && onHighFive(m.id); }}
        />
      )}
    </div>
  );
}
